import { useEffect, useState } from 'react';
import {
  ButtonCta,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogHeading,
  DialogRoot,
  DialogTrigger,
  Icon,
  LinkCta,
  Stack,
  Text,
} from '@global-ecom/nitro-uds/elements';

import { useFeature } from 'hooks/useFeature';

type Step = {
  text: string;
  description?: string;
};

export const KlarnaInfo = () => {
  const showDisclaimerMessage = useFeature('SHOW_KLARNA_DISCLAIMER_MESSAGE');
  const [isOpen, setIsOpen] = useState(false);

  const steps: Step[] = [
    { text: 'At checkout select Klarna' },
    {
      text: 'Choose your payment plan',
      description:
        'Different payment plans may be shown depending on the purchase amount and credit score. ',
    },
    {
      text: 'Complete your checkout',
      description:
        'The amount will be charged based on the payment plan you choose.',
    },
  ];

  // Adjusts the top property of the uds close button inside
  // the DialogHeader to align it with Klarna Icon
  useEffect(() => {
    if (isOpen) {
      const svgIcon = document.querySelector(
        '[data-uds-child="dialog-header"] button[data-uds-child="button-icon"]'
      );
      if (svgIcon) {
        svgIcon.classList.add('top-7');
      }
    }
  }, [isOpen]);

  return (
    <DialogRoot
      drawer={false}
      onOpenChange={() => setIsOpen(!isOpen)}
      align={{ xs: 'center' }}
      justify={{ xs: 'center' }}
      size={{ xs: 'xs' }}
      dataTestId="klarna-modal"
    >
      <DialogTrigger>
        <button
          type="button"
          className="h-[24px] px-1 py-[9px] hover:(border-[#8C9198] border-2) border-2 border border-[#DFE0E1] rounded-sm flex items-center mx-1"
          data-test-id="klarna-button-id"
        >
          <Icon
            name="payment-klarna"
            className="h-[24px]! w-[40px]!"
            data-test-id="klarna-button-icon-id"
            label="klarna-payment-info"
          />
        </button>
      </DialogTrigger>
      <DialogContent className="fixed bottom-0 right-0 xs:(relative bottom-auto right-auto) w-full pt-1">
        <DialogHeader>
          <DialogHeading>
            <Icon
              name="payment-klarna-inline"
              size="9xl"
              aspectRatio={0.5}
              data-test-id="klarna-icon-id"
            />
          </DialogHeading>
        </DialogHeader>
        <DialogBody>
          <Stack direction="column" gapY="lg" className="pb-2">
            <Stack gapY="none">
              <Text weight="bold" size="xl">
                Buy Now. Pay with Klarna
              </Text>
              <Text size="base">Get what you love, choose how to pay.</Text>
            </Stack>
            <Stack
              gapY="none"
              className="bg-neutral-10 px-[46px] pt-4 py-6 rounded"
              data-test-id="klarna-how-it-works-id"
            >
              <Text weight="bold" size="lg" className="mb-2.5">
                How it Works
              </Text>
              <ul className="flex flex-col gap-y-6 relative">
                <div className="absolute top-1 left-[3px] h-40 w-0.5 bg-neutral-60 z-0" />
                {steps.map((item, index) => (
                  <li key={index} className="flex items-start gap-x-3">
                    <div className="z-10 bg-neutral-10 py-3">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="8"
                        height="8"
                        viewBox="0 0 8 8"
                        fill="none"
                      >
                        <circle cx="4" cy="4" r="4" fill="#676D75" />
                      </svg>
                    </div>
                    <Stack direction="column" gapY="none" className="my-[3px]">
                      <Text size="base" className="text-neutral-60">
                        {item.text}
                      </Text>
                      <Text size="xs" className="text-neutral-60 text-balance">
                        {item.description}
                      </Text>
                    </Stack>
                  </li>
                ))}
              </ul>
            </Stack>
            {showDisclaimerMessage && (
              <Text
                size="xxs"
                className="text-neutral-60"
                data-test-id="klarna-terms-id"
              >
                *See payment{' '}
                <a
                  href="https://cdn.klarna.com/1.0/shared/content/legal/terms/0/en_us/sliceitinx#2"
                  target="_blank"
                  className="underline underline-offset-[2px]"
                  rel="noreferrer"
                >
                  terms
                </a>
                . A higher initial payment may be required for some consumers.
                CA resident loans made or arranged pursuant to a California
                Financing Law license.
              </Text>
            )}
          </Stack>
        </DialogBody>
        <DialogFooter className="flex flex-col">
          <DialogTrigger>
            <ButtonCta label="Close" data-test-id="close-button-id" />
          </DialogTrigger>
          <LinkCta label="Learn more" variant="secondary" href="/help/klarna" />
        </DialogFooter>
      </DialogContent>
    </DialogRoot>
  );
};
